/**
 * @packageDocumentation
 * @module      CorPageSystemTester.ts
 * @description The System Tester page in the cloud application
 * @copyright   Copyright © 2025 Siemens Healthineers. All rights reserved.
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var CorPageSystemTester_1;
import { LitElement, customElement, html, css, property } from 'lit-element';
import CorThemeSelector from "./CorThemeSelector";
import { ParentTestResult } from './ParentTestResult';
import { TestStatus } from './TestStatus';
import { SystemTesterDisplayFacade } from './emc/SystemTesterDisplayFacade';
import EmcConsts from './emc/EmcConsts';
import SystemTesterConsts from "./emc/SystemTesterConsts";
import { TEST_CONFIGURATIONS } from './SystemTesterConfig';
const NOT_AVAILABLE_TEXT = 'Not Available';
let CorPageSystemTester = CorPageSystemTester_1 = class CorPageSystemTester extends LitElement {
    constructor() {
        super(...arguments);
        this.systemTesterDisplayFacade = SystemTesterDisplayFacade.getSystemTesterDisplayFacade();
        this.currentTheme = this.resolveTheme();
        this.hasWarning = false;
        this.automatedMovesStartTimer = null;
        this.warningModalTitle = "";
        this.addingSystems = false;
        this.areSystemTestsRunning = false;
        this.activeSystemsList = this.systemTesterDisplayFacade.activeSystemsList;
        this.controlSystemCount = this.systemTesterDisplayFacade.controlSystemCount;
        this.patientSystemCount = this.systemTesterDisplayFacade.patientSystemCount;
        this.currentRunningTest = 0;
        this.startSystemTestMonitorTimeStamp = this.systemTesterDisplayFacade.startSystemTestMonitorTimeStamp;
        this.messageText = "";
        this.logEntries = [];
        this.disabledStates = [];
        this.disabledPasses = [];
        this.disabledFailures = [];
        this.callAfterWarningClose = null;
        this.testEndedMessageDisabled = true;
        this.systemTestRuntime = this.systemTesterDisplayFacade.systemTestRuntime;
        this.subsystemSerialNumbers = this.systemTesterDisplayFacade.getSubsystemSerialNumbers();
        this.serialNumber = '';
        this.isSerialNumberLocked = false;
        this.isSubsystemSerialNumbersLocked = false;
        this.parentTestResults = [];
        this.testerFirstName = '';
        this.testerLastName = '';
        this.isTesterNameLocked = false;
        this.automatedMovesTimer = null;
        this.worker = (() => {
            const setInterval = `
            setInterval(() => {
                postMessage("updateProperties");
            }, ${EmcConsts.REFRESH_INTERVAL})
        `;
            const blob = new Blob([setInterval], { type: "text/javascript" });
            return new Worker(URL.createObjectURL(blob));
        })();
        this.handleBeforeUnload = (event) => {
            this.exportLogsAsPdf();
        };
    }
    static get styles() {
        return css `
            .my-button {
                --sh-button-width-size-s: 70px;
            }
        `;
    }
    get testLimit() {
        return TEST_CONFIGURATIONS.length - 2;
    }
    convertToCSV(data) {
        return data.split('\n').map(row => row.replace(/,/g, ' ')).join('\n');
    }
    calculateLineHeight(doc) {
        const fontSize = doc.getFontSize();
        const LINE_HEIGHT_MULTIPLIER = 0.35;
        return fontSize * LINE_HEIGHT_MULTIPLIER;
    }
    exportLogsAsPdf() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const { jsPDF } = window.jspdf;
            const doc = new jsPDF();
            const PAGE_MARGIN = 10;
            const MAX_LINE_WIDTH = 180;
            const BOTTOM_PAGE_LIMIT = 280;
            const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500MB
            let faultData = ((_a = this.messageText) === null || _a === void 0 ? void 0 : _a.toString()) || "";
            let blob = new Blob([faultData], { type: 'text/plain' });
            if (blob.size > MAX_FILE_SIZE) {
                const notice = SystemTesterConsts.truncatedLogMessage;
                const noticeBlob = new Blob([notice], { type: 'text/plain' });
                blob = blob.slice(blob.size - (MAX_FILE_SIZE - noticeBlob.size));
                blob = new Blob([noticeBlob, blob], { type: 'text/plain' });
                faultData = yield blob.text();
            }
            const startTime = this.startSystemTestMonitorTimeStamp
                ? new Date(this.startSystemTestMonitorTimeStamp)
                : new Date();
            const timestamp = `${startTime.getFullYear()}${String(startTime.getMonth() + 1).padStart(2, '0')}${String(startTime.getDate()).padStart(2, '0')}-` +
                `${String(startTime.getHours()).padStart(2, '0')}${String(startTime.getMinutes()).padStart(2, '0')}${String(startTime.getSeconds()).padStart(2, '0')}`;
            const fileName = `SysTester-${timestamp}.pdf`;
            const lines = doc.splitTextToSize(faultData, MAX_LINE_WIDTH);
            const lineHeight = this.calculateLineHeight(doc);
            let currentYPosition = PAGE_MARGIN;
            lines.forEach((line) => {
                if (currentYPosition > BOTTOM_PAGE_LIMIT) {
                    doc.addPage();
                    currentYPosition = PAGE_MARGIN;
                }
                doc.text(line, PAGE_MARGIN, currentYPosition, { align: "left" });
                currentYPosition += lineHeight;
            });
            doc.save(fileName);
        });
    }
    get subsystemNames() {
        return this.systemTesterDisplayFacade.getModuleNames();
    }
    getSubsystemSerialNumbersForSave() {
        const subsystemSerialNumbersForSave = {};
        this.subsystemNames.forEach((subsystemName) => {
            const subsystemSerialNumber = this.subsystemSerialNumbers[subsystemName];
            subsystemSerialNumbersForSave[subsystemName] = subsystemSerialNumber;
        });
        return subsystemSerialNumbersForSave;
    }
    renderFaultMessageLines() {
        return html `
            <div
                style="
                    width: 100%;
                    height: 100%;
                    overflow-y: auto;
                    font-size: 16px;
                    font-family: monospace;
                    border: 1px solid #d8dde6;
                    border-radius: 4px;
                    padding: 6px;
                    box-sizing: border-box;
                    background: white;
                "
            >
                ${this.logEntries.map((entry) => html `<div style="background-color: ${entry.kind === 'fault' ? '#fff59d' : entry.kind === 'status' ? '#d0d0d0' : 'transparent'}; padding: 1px 2px; white-space: pre-wrap;">${entry.text}</div>`)}
            </div>
        `;
    }
    connectedCallback() {
        super.connectedCallback();
        if (this.disabledStates.length === 0) {
            this.disabledStates = Array(this.testLimit).fill(true);
            this.disabledPasses = Array(this.testLimit).fill(true);
            this.disabledFailures = Array(this.testLimit).fill(true);
        }
        this.parentTestResults = this.systemTesterDisplayFacade.parentTestResults;
        this.areSystemTestsRunning = this.systemTesterDisplayFacade.areSystemTestsRunning;
        if (this.areSystemTestsRunning) {
            this.messageText = this.systemTesterDisplayFacade.messageText;
            this.logEntries = [...this.systemTesterDisplayFacade.logEntries];
        }
        else {
            this.systemTesterDisplayFacade.messageText = "";
            this.systemTesterDisplayFacade.logEntries = [];
        }
        if (this.parentTestResults.length === 0) {
            this.parentTestResults = this.createParentTestResults();
        }
        window.addEventListener("beforeunload", this.handleBeforeUnload);
        this.worker.onmessage = (event) => {
            if (event.data == "updateProperties") {
                this.updateProperties();
            }
        };
        this.currentRunningTest = this.systemTesterDisplayFacade.currentRunningTest;
        this.syncStateToFacade();
    }
    syncStateToFacade() {
        this.systemTesterDisplayFacade.startSystemTestMonitorTimeStamp = this.startSystemTestMonitorTimeStamp;
        this.systemTesterDisplayFacade.patientSystemCount = this.patientSystemCount;
        this.systemTesterDisplayFacade.controlSystemCount = this.controlSystemCount;
        this.systemTesterDisplayFacade.activeSystemsList = this.activeSystemsList;
        this.systemTesterDisplayFacade.disabledStates = [...this.disabledStates];
        this.systemTesterDisplayFacade.disabledPasses = [...this.disabledPasses];
        this.systemTesterDisplayFacade.disabledFailures = [...this.disabledFailures];
        this.systemTesterDisplayFacade.areSystemTestsRunning = this.areSystemTestsRunning;
        this.systemTesterDisplayFacade.currentRunningTest = this.currentRunningTest;
        this.systemTesterDisplayFacade.parentTestResults = this.parentTestResults;
    }
    updateProperties() {
        this.messageText = this.systemTesterDisplayFacade.messageText;
        this.logEntries = [...this.systemTesterDisplayFacade.logEntries];
        this.areSystemTestsRunning = this.systemTesterDisplayFacade.areSystemTestsRunning;
        this.currentRunningTest = this.systemTesterDisplayFacade.currentRunningTest;
        this.isTesterNameLocked = this.systemTesterDisplayFacade.isTesterNameLocked;
        this.isSerialNumberLocked = this.systemTesterDisplayFacade.isSerialNumberLocked;
        this.isSubsystemSerialNumbersLocked = this.systemTesterDisplayFacade.isSubsystemSerialNumbersLocked;
        if (this.systemTesterDisplayFacade.isTesterNameLocked) {
            this.testerFirstName = this.systemTesterDisplayFacade.getTesterFirstName();
            this.testerLastName = this.systemTesterDisplayFacade.getTesterLastName();
        }
        if (this.systemTesterDisplayFacade.isSerialNumberLocked) {
            this.serialNumber = this.systemTesterDisplayFacade.getSerialNumber();
        }
        if (this.systemTesterDisplayFacade.isSubsystemSerialNumbersLocked) {
            this.subsystemSerialNumbers = this.systemTesterDisplayFacade.getSubsystemSerialNumbers();
        }
        this.systemTestRuntime = this.systemTesterDisplayFacade.systemTestRuntime;
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        if (this.areSystemTestsRunning) {
            this.stopTests();
            return;
        }
        this.syncStateToFacade();
        if (this.areSystemTestsRunning) {
            this.exportLogsAsPdf();
        }
    }
    resolveTheme() {
        return CorThemeSelector.getTheme();
    }
    startAutomatedMovesCompletionTimer() {
        if (this.automatedMovesTimer) {
            clearTimeout(this.automatedMovesTimer);
        }
        this.automatedMovesTimer = setTimeout(() => {
            this.showAutomatedMovesCompleteMessage();
        }, 2 * 60 * 1000); // 2 minutes
    }
    showAutomatedMovementWarning() {
        const message = "STAND BACK, AUTOMATED MOVEMENTS STARTING WITHIN 30 SECONDS ";
        this.hasWarning = true;
        this.warningModalTitle = message;
        const now = new Date().toLocaleString("en-US");
        this.messageText = `${now} ${message}\n` + this.messageText;
        this.logEntries = [{ text: `${now} ${message}`, kind: 'normal' }, ...this.logEntries];
    }
    sendSignalsForTestEnd() {
        this.systemTesterDisplayFacade.setEmcAutomationSignal(false);
        this.systemTesterDisplayFacade.setFullSystemSignal(false);
    }
    startAutomatedMovesAfterDelay() {
        if (this.automatedMovesStartTimer) {
            clearTimeout(this.automatedMovesStartTimer);
        }
        this.automatedMovesStartTimer = setTimeout(() => {
            this.systemTesterDisplayFacade.setEmcAutomationSignal(true);
            this.systemTesterDisplayFacade.setFullSystemSignal(true);
            this.startAutomatedMovesCompletionTimer();
        }, 30 * 1000); // 30 seconds
    }
    showAutomatedMovesCompleteMessage() {
        const message = "AUTOMATED MOVES COMPLETE";
        const now = new Date().toLocaleString("en-US");
        this.sendSignalsForTestEnd();
        if (this.automatedMovesTimer) {
            clearTimeout(this.automatedMovesTimer);
        }
        this.automatedMovesTimer = setTimeout(() => {
            // Display warning/info modal
            this.hasWarning = true;
            this.warningModalTitle = message;
            // Log to message history
            this.messageText = `${now} ${message}\n` + this.messageText;
            this.logEntries = [{ text: `${now} ${message}`, kind: 'normal' }, ...this.logEntries];
            this.requestUpdate();
        }, 10 * 1000); // 10 seconds
    }
    createParentTestResults() {
        return TEST_CONFIGURATIONS.slice(1, -1).map((config, index) => new ParentTestResult(`parent-test-${index}`, config.name));
    }
    getParentTestDisplayName(testIndex) {
        var _a, _b;
        return (_b = (_a = TEST_CONFIGURATIONS[testIndex + 1]) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : `Test ${testIndex + 1}`;
    }
    initializeTestResults() {
        this.parentTestResults = this.createParentTestResults();
        this.disabledStates.fill(true);
        this.disabledFailures.fill(true);
        this.disabledPasses.fill(true);
        this.disabledStates[this.currentRunningTest] = false;
        const currentParentTestResult = this.getCurrentParentTestResult();
        if (currentParentTestResult) {
            currentParentTestResult.markAsStarted();
        }
    }
    getCurrentParentTestResult() {
        return this.parentTestResults[this.currentRunningTest];
    }
    isCurrentParentTest(testIndex) {
        return this.areSystemTestsRunning && testIndex === this.currentRunningTest;
    }
    updateParentStatusIndicators(testIndex) {
        const parentTestResult = this.parentTestResults[testIndex];
        if (!parentTestResult) {
            return;
        }
        const aggregatedStatus = parentTestResult.getAggregatedStatus();
        this.disabledPasses[testIndex] = aggregatedStatus !== TestStatus.PASS;
        this.disabledFailures[testIndex] = aggregatedStatus !== TestStatus.FAIL;
    }
    logParentTestResult(parentTestName, aggregatedStatus) {
        if (aggregatedStatus === TestStatus.PASS) {
            this.systemTesterDisplayFacade.addMessageToMessageDisplay(`${parentTestName}${SystemTesterConsts.parentTestPassed}`);
            return;
        }
        if (aggregatedStatus === TestStatus.FAIL) {
            this.systemTesterDisplayFacade.addMessageToMessageDisplay(`${parentTestName}${SystemTesterConsts.parentTestFailed}`);
            return;
        }
        this.systemTesterDisplayFacade.addMessageToMessageDisplay(`${parentTestName}${SystemTesterConsts.parentTestSkipped}`);
    }
    advanceToNextParentTest() {
        this.currentRunningTest += 1;
        const homingIndex = TEST_CONFIGURATIONS.findIndex(c => c.name === "Homing - Automated Moves");
        if (homingIndex >= 0 && this.currentRunningTest + 1 === homingIndex) {
            this.showAutomatedMovementWarning();
            this.startAutomatedMovesAfterDelay();
        }
        if (this.currentRunningTest < this.testLimit) {
            const nextTestConfig = TEST_CONFIGURATIONS[this.currentRunningTest + 1];
            if (nextTestConfig) {
                this.systemTesterDisplayFacade.addMessageToMessageDisplay(nextTestConfig.startMessage);
            }
            this.disabledStates[this.currentRunningTest] = false;
            const nextParentTest = this.getCurrentParentTestResult();
            if (nextParentTest) {
                nextParentTest.markAsStarted();
            }
            return;
        }
        this.completeTestRun();
    }
    completeCurrentParentTest(status) {
        const currentParentTestResult = this.getCurrentParentTestResult();
        if (!currentParentTestResult) {
            return;
        }
        if (status === TestStatus.PASS) {
            currentParentTestResult.markAsPassed();
        }
        else if (status === TestStatus.FAIL) {
            currentParentTestResult.markAsFailed();
        }
        else {
            currentParentTestResult.markAsSkipped();
        }
        this.updateParentStatusIndicators(this.currentRunningTest);
        this.logParentTestResult(currentParentTestResult.name, status);
        this.advanceToNextParentTest();
        this.messageText = this.systemTesterDisplayFacade.messageText;
        this.logEntries = [...this.systemTesterDisplayFacade.logEntries];
        this.syncStateToFacade();
        this.requestUpdate();
    }
    completeTestRun() {
        this.currentRunningTest = 0;
        this.areSystemTestsRunning = false;
        this.testEndedMessageDisabled = false;
        this.disabledStates.fill(true);
        if (this.automatedMovesTimer) {
            clearTimeout(this.automatedMovesTimer);
            this.automatedMovesTimer = null;
        }
        if (this.automatedMovesStartTimer) {
            clearTimeout(this.automatedMovesStartTimer);
            this.automatedMovesStartTimer = null;
        }
        this.disabledPasses.fill(true);
        this.disabledFailures.fill(true);
        this.parentTestResults = this.createParentTestResults();
        this.systemTesterDisplayFacade.setIdleMonitorSignal(false);
        this.systemTesterDisplayFacade.setEmcAutomationSignal(false);
        this.systemTesterDisplayFacade.setFullSystemSignal(false);
        const finishedMessage = TEST_CONFIGURATIONS[TEST_CONFIGURATIONS.length - 1].startMessage;
        this.systemTesterDisplayFacade.addMessageToMessageDisplay(finishedMessage);
        this.messageText = this.systemTesterDisplayFacade.messageText;
        this.logEntries = [...this.systemTesterDisplayFacade.logEntries];
        this.syncStateToFacade();
        this.exportLogsAsPdf();
        this.systemTesterDisplayFacade.messageText = "";
        this.systemTesterDisplayFacade.logEntries = [];
        this.messageText = "";
        this.logEntries = [];
        this.resetSerialNumberForNewSession();
    }
    onClickPass() {
        this.completeCurrentParentTest(TestStatus.PASS);
    }
    initiateAddOrRemoveSystems() {
        this.addingSystems = true;
        this.systemTesterDisplayFacade.populateAllSystemList().then(() => this.requestUpdate());
    }
    closeAddOrRemoveSystems() {
        this.addingSystems = false;
    }
    getActiveSystemsListDisplay() {
        if (this.activeSystemsList.length === 0) {
            return html ``;
        }
        return html `
        <sh-card style="width: 100%;">
            <sh-table ?readonly="false" id="system-tester-systems-table">
                <sh-table-row slot="header">
                    <sh-table-head unsortable columns="${CorPageSystemTester_1.COLUMN_WIDTH}">Name</sh-table-head>
                    <sh-table-head unsortable columns="${CorPageSystemTester_1.COLUMN_WIDTH}">System Id</sh-table-head>
                    <sh-table-head unsortable columns="${CorPageSystemTester_1.COLUMN_WIDTH}">Type</sh-table-head>
                </sh-table-row>
                ${this.activeSystemsList.map((system) => html `
                    <sh-table-row columns="${CorPageSystemTester_1.TOTAL_COLUMNS}">
                        <sh-table-cell columns="${CorPageSystemTester_1.COLUMN_WIDTH}">
                            <sh-text>${system.systemName}</sh-text>
                        </sh-table-cell>
                        <sh-table-cell columns="${CorPageSystemTester_1.COLUMN_WIDTH}">
                            <sh-text>${system.systemId}</sh-text>
                        </sh-table-cell>
                        <sh-table-cell columns="${CorPageSystemTester_1.COLUMN_WIDTH}">
                            <sh-text>${system.isControlSide ? 'Control-side' : 'Patient-side'}</sh-text>
                        </sh-table-cell>
                    </sh-table-row>`)}
            </sh-table>
        </sh-card>`;
    }
    onClickFail() {
        this.completeCurrentParentTest(TestStatus.FAIL);
    }
    onClickSkip() {
        this.completeCurrentParentTest(TestStatus.SKIPPED);
    }
    logStartSystemTests() {
        if (this.systemTesterDisplayFacade.messageText.toString().trim().length > 0) {
            this.systemTesterDisplayFacade.addMessageToMessageDisplay('');
        }
        const headerLines = [];
        const fullTesterName = [this.testerFirstName, this.testerLastName].filter(n => n).join(' ');
        if (fullTesterName) {
            headerLines.push(`User Name: ${fullTesterName}`);
        }
        const serialNumber = this.systemTesterDisplayFacade.getSerialNumber();
        if (serialNumber) {
            headerLines.push(`Serial Number: ${serialNumber}`);
        }
        const subsystemSerialNumbers = this.systemTesterDisplayFacade.getSubsystemSerialNumbers();
        const subsystemEntries = Object.entries(subsystemSerialNumbers);
        if (subsystemEntries.length > 0) {
            headerLines.push('Subsystem Serial Numbers -');
            subsystemEntries.forEach(([name, sn]) => {
                headerLines.push(`${name} Serial Number: ${sn || 'Not Available'}`);
            });
        }
        if (headerLines.length > 0) {
            this.systemTesterDisplayFacade.logEntries.push(...headerLines.map(line => ({ text: line, kind: 'normal' })));
            this.systemTesterDisplayFacade.messageText = this.systemTesterDisplayFacade.messageText.toString() + headerLines.join('\n') + '\n';
        }
        let currentDateTime = new Date().toLocaleString("en-US");
        this.systemTesterDisplayFacade.addMessageToMessageDisplay(SystemTesterConsts.startSystemTests);
    }
    logStopSystemTests() {
        let currentDateTime = new Date();
        this.areSystemTestsRunning = false;
        this.systemTesterDisplayFacade.addMessageToMessageDisplay(SystemTesterConsts.stopSystemTests);
        this.messageText = this.systemTesterDisplayFacade.messageText;
        this.logEntries = [...this.systemTesterDisplayFacade.logEntries];
        this.exportLogsAsPdf();
    }
    resetSerialNumberForNewSession() {
        this.systemTesterDisplayFacade.resetAllSerialNumbers();
        this.testerFirstName = '';
        this.testerLastName = '';
        this.isTesterNameLocked = false;
        this.serialNumber = '';
        this.subsystemSerialNumbers = this.systemTesterDisplayFacade.getSubsystemSerialNumbers();
        this.isSerialNumberLocked = false;
        this.isSubsystemSerialNumbersLocked = false;
    }
    stopTests() {
        this.areSystemTestsRunning = false;
        this.testEndedMessageDisabled = false;
        this.systemTesterDisplayFacade.previousPackets.clear();
        this.systemTesterDisplayFacade.hasTestEnded = true;
        this.disabledStates.fill(true);
        this.disabledPasses.fill(true);
        this.disabledFailures.fill(true);
        this.parentTestResults.forEach(parentTestResult => {
            if (parentTestResult.status === TestStatus.PENDING) {
                this.logParentTestResult(parentTestResult.name, TestStatus.SKIPPED);
            }
        });
        // Reset parent test results for next session
        this.parentTestResults = this.createParentTestResults();
        this.systemTesterDisplayFacade.setIdleMonitorSignal(false);
        this.systemTesterDisplayFacade.setEmcAutomationSignal(false);
        this.systemTesterDisplayFacade.setFullSystemSignal(true);
        this.currentRunningTest = 0;
        if (this.automatedMovesTimer) {
            clearTimeout(this.automatedMovesTimer);
            this.automatedMovesTimer = null;
        }
        if (this.automatedMovesStartTimer) {
            clearTimeout(this.automatedMovesStartTimer);
            this.automatedMovesStartTimer = null;
        }
        this.hasWarning = false;
        this.warningModalTitle = "";
        this.logStopSystemTests();
        this.systemTesterDisplayFacade.messageText = "";
        this.systemTesterDisplayFacade.logEntries = [];
        this.messageText = "";
        this.logEntries = [];
        this.startSystemTestMonitorTimeStamp = 0;
        this.resetSerialNumberForNewSession();
        this.syncStateToFacade();
        this.requestUpdate();
    }
    startTests() {
        this.systemTesterDisplayFacade.previousPackets.clear();
        // Auto-save tester name
        this.systemTesterDisplayFacade.setTesterName(this.testerFirstName, this.testerLastName);
        this.isTesterNameLocked = this.systemTesterDisplayFacade.isTesterNameLocked;
        // Auto-save serial number
        this.systemTesterDisplayFacade.setSerialNumber(this.serialNumber);
        this.isSerialNumberLocked = this.systemTesterDisplayFacade.isSerialNumberLocked;
        if (this.isSerialNumberLocked) {
            this.serialNumber = this.systemTesterDisplayFacade.getSerialNumber();
        }
        // Auto-save subsystem serial numbers
        this.systemTesterDisplayFacade.setSubsystemSerialNumbers(this.getSubsystemSerialNumbersForSave());
        this.isSubsystemSerialNumbersLocked = this.systemTesterDisplayFacade.isSubsystemSerialNumbersLocked;
        if (this.isSubsystemSerialNumbersLocked) {
            this.subsystemSerialNumbers = this.systemTesterDisplayFacade.getSubsystemSerialNumbers();
        }
        this.systemTesterDisplayFacade.populateAllSystemList().then(() => {
            let systemIdsNotBeingUsedAnywhereElse = new Set();
            let toBeRemovedActiveSystems = [];
            this.systemTesterDisplayFacade.allSystemsList.forEach(system => systemIdsNotBeingUsedAnywhereElse.add(system.systemId));
            this.activeSystemsList.forEach(currentSystem => {
                if (!systemIdsNotBeingUsedAnywhereElse.has(currentSystem.systemId)) {
                    toBeRemovedActiveSystems.push(currentSystem);
                }
            });
            if (toBeRemovedActiveSystems.length > 0) {
                this.hasWarning = true;
                this.warningModalTitle = "Some active system(s) are in use elsewhere";
                toBeRemovedActiveSystems.forEach(sys => {
                    this.activeSystemsList.splice(this.activeSystemsList.findIndex(s => s.systemId === sys.systemId), 1);
                    sys.isControlSide ? this.controlSystemCount-- : this.patientSystemCount--;
                });
                return;
            }
            if (this.activeSystemsList.length === 0) {
                this.hasWarning = true;
                this.warningModalTitle = "Select systems before starting with tests";
                this.callAfterWarningClose = () => { this.addingSystems = true; };
                return;
            }
            this.areSystemTestsRunning = true;
            this.systemTesterDisplayFacade.hasTestEnded = false;
            this.testEndedMessageDisabled = true;
            this.startSystemTestMonitorTimeStamp = Date.now();
            this.currentRunningTest = 0;
            this.initializeTestResults();
            this.logStartSystemTests();
            const firstTestConfig = TEST_CONFIGURATIONS[this.currentRunningTest + 1];
            if (firstTestConfig) {
                this.systemTesterDisplayFacade.addMessageToMessageDisplay(firstTestConfig.startMessage);
            }
            this.systemTesterDisplayFacade.setIdleMonitorSignal(true);
            this.messageText = this.systemTesterDisplayFacade.messageText;
            this.logEntries = [...this.systemTesterDisplayFacade.logEntries];
            this.syncStateToFacade();
            this.requestUpdate();
        });
    }
    onClickStartStop() {
        if (this.areSystemTestsRunning) {
            this.stopTests();
            return;
        }
        if (this.shouldDisableStartSystemTests()) {
            this.hasWarning = true;
            this.warningModalTitle = "Please fill in Tester Name, Serial Number, and all Subsystem Serial Numbers before starting tests";
            return;
        }
        this.startTests();
    }
    patientSideSelected(system) {
        if (this.patientSystemCount < 1) {
            this.patientSystemCount++;
            this.activeSystemsList = [...this.activeSystemsList, system];
        }
        else if (this.patientSystemCount >= 1) {
            this.addingSystems = false;
            this.hasWarning = true;
            this.warningModalTitle = "Only 1 Patient-Side system can be selected";
            this.callAfterWarningClose = () => { this.addingSystems = true; };
        }
    }
    controlSideSelected(system) {
        if (this.controlSystemCount < 1) {
            this.controlSystemCount++;
            this.activeSystemsList = [...this.activeSystemsList, system];
        }
        else if (this.controlSystemCount >= 1) {
            this.addingSystems = false;
            this.hasWarning = true;
            this.warningModalTitle = "Only 1 Control-Side system can be selected";
            this.callAfterWarningClose = () => { this.addingSystems = true; };
        }
    }
    systemSelectionChanged(e, system) {
        // @ts-ignore
        if (e.currentTarget.active) {
            if (this.activeSystemsList.length == 2) {
                this.addingSystems = false;
                this.hasWarning = true;
                this.warningModalTitle = "More than 2 systems cannot be selected";
                this.callAfterWarningClose = () => { this.addingSystems = true; };
            }
            else {
                if (system.isControlSide) {
                    this.controlSideSelected(system);
                }
                else {
                    this.patientSideSelected(system);
                }
            }
            //@ts-ignore
            e.currentTarget.active = false;
        }
        else {
            if (system.isControlSide) {
                this.controlSystemCount--;
            }
            else {
                this.patientSystemCount--;
            }
            this.activeSystemsList = this.activeSystemsList.filter(activeSystem => activeSystem.systemId !== system.systemId);
        }
        this.activeSystemsList = [...this.activeSystemsList].sort((a, b) => a.systemName.localeCompare(b.systemName));
        this.requestUpdate();
    }
    closeWarningBox() {
        this.hasWarning = false;
        if (this.callAfterWarningClose)
            this.callAfterWarningClose();
        this.callAfterWarningClose = null;
        this.requestUpdate();
    }
    normalizeAlphanumericSerialInput(e) {
        const target = e.target;
        const alphanumericValue = target.value.replace(/[^a-zA-Z0-9]/g, '');
        if (target.value !== alphanumericValue) {
            target.value = alphanumericValue;
        }
        return alphanumericValue;
    }
    areAllSubsystemSerialNumbersFilled() {
        return this.subsystemNames.every((subsystemName) => {
            return this.subsystemSerialNumbers[subsystemName] && this.subsystemSerialNumbers[subsystemName].trim().length > 0;
        });
    }
    areAllRequiredFieldsSaved() {
        const hasTesterName = (this.testerFirstName || '').trim().length > 0 &&
            (this.testerLastName || '').trim().length > 0;
        const hasPrimarySerialNumber = (this.serialNumber || '').trim().length > 0;
        const hasAllSubsystemSerialNumbers = this.areAllSubsystemSerialNumbersFilled();
        return hasTesterName &&
            hasPrimarySerialNumber &&
            hasAllSubsystemSerialNumbers;
    }
    shouldDisableStartSystemTests() {
        if (this.areSystemTestsRunning) {
            return false;
        }
        return !this.areAllRequiredFieldsSaved();
    }
    handleSerialNumberInput(e) {
        this.serialNumber = this.normalizeAlphanumericSerialInput(e);
    }
    handleSubsystemSerialNumberInput(e, subsystemName) {
        this.subsystemSerialNumbers = Object.assign(Object.assign({}, this.subsystemSerialNumbers), { [subsystemName]: this.normalizeAlphanumericSerialInput(e) });
    }
    submitSerialNumbersForSection(isSubsystemSection) {
        if (isSubsystemSection) {
            if (this.isSubsystemSerialNumbersLocked) {
                return;
            }
            this.systemTesterDisplayFacade.setSubsystemSerialNumbers(this.getSubsystemSerialNumbersForSave());
            this.isSubsystemSerialNumbersLocked = this.systemTesterDisplayFacade.isSubsystemSerialNumbersLocked;
            if (this.isSubsystemSerialNumbersLocked) {
                this.subsystemSerialNumbers = this.systemTesterDisplayFacade.getSubsystemSerialNumbers();
            }
            this.requestUpdate();
            return;
        }
        if (this.isSerialNumberLocked) {
            return;
        }
        this.systemTesterDisplayFacade.setSerialNumber(this.serialNumber);
        this.isSerialNumberLocked = this.systemTesterDisplayFacade.isSerialNumberLocked;
        if (this.isSerialNumberLocked) {
            this.serialNumber = this.systemTesterDisplayFacade.getSerialNumber();
        }
        this.requestUpdate();
    }
    submitSerialNumber() {
        this.submitSerialNumbersForSection(false);
    }
    submitSubsystemSerialNumbers() {
        if (!this.areAllSubsystemSerialNumbersFilled()) {
            return;
        }
        this.submitSerialNumbersForSection(true);
    }
    handleTesterFirstNameInput(e) {
        const target = e.target;
        this.testerFirstName = target.value;
    }
    handleTesterLastNameInput(e) {
        const target = e.target;
        this.testerLastName = target.value;
    }
    submitTesterName() {
        if (this.isTesterNameLocked) {
            return;
        }
        if (this.testerFirstName && this.testerLastName) {
            this.systemTesterDisplayFacade.setTesterName(this.testerFirstName, this.testerLastName);
            this.isTesterNameLocked = this.systemTesterDisplayFacade.isTesterNameLocked;
            if (this.isTesterNameLocked) {
                this.testerFirstName = this.systemTesterDisplayFacade.getTesterFirstName();
                this.testerLastName = this.systemTesterDisplayFacade.getTesterLastName();
            }
            this.requestUpdate();
        }
    }
    getCurrentInstruction() {
        if (!this.areSystemTestsRunning) {
            const config = (this.testEndedMessageDisabled || !this.shouldDisableStartSystemTests())
                ? TEST_CONFIGURATIONS[0]
                : TEST_CONFIGURATIONS[TEST_CONFIGURATIONS.length - 1];
            return html `
                <sh-text size="header-1" color="primary">
                    ${config.instruction}
                </sh-text>
            `;
        }
        // During tests
        const currentConfig = TEST_CONFIGURATIONS[this.currentRunningTest + 1];
        const instruction = (currentConfig === null || currentConfig === void 0 ? void 0 : currentConfig.instruction) || '';
        return html `
            <sh-text size="header-1" color="primary">
                ${instruction}${this.currentRunningTest === this.testLimit - 1
            ? ' WARNING: This test has automatic moving machinery. Stand clear and check the work area.'
            : ''}
            </sh-text>
        `;
    }
    renderInstructionImages() {
        const imgStyle = "flex: 1; min-height: 0; width: 100%; object-fit: contain;";
        const refLabel = html `<sh-text size="body-2" style="text-align: center; flex-shrink: 0; color: black;">Image for Reference Only</sh-text>`;
        if (!this.areSystemTestsRunning) {
            const config = (this.testEndedMessageDisabled || !this.shouldDisableStartSystemTests())
                ? TEST_CONFIGURATIONS[0]
                : TEST_CONFIGURATIONS[TEST_CONFIGURATIONS.length - 1];
            const images = config.images;
            if (images.length > 1) {
                return html `
                    ${refLabel}
                    <div style="display: flex; flex-direction: column; gap: 8px; flex: 1; min-height: 0; width: 100%;">
                        ${images.map(src => html `
                            <img src="${src}" style="flex: 1; min-height: 0; width: 100%; object-fit: contain;" alt="${config.name}" />
                        `)}
                    </div>
                `;
            }
            return html `
                ${refLabel}
                <img src="${images[0]}" style="${imgStyle}" alt="${config.name}" />
            `;
        }
        else if (this.areSystemTestsRunning) {
            const currentConfig = TEST_CONFIGURATIONS[this.currentRunningTest + 1];
            const images = (currentConfig === null || currentConfig === void 0 ? void 0 : currentConfig.images) || [];
            if (images.length > 1) {
                return html `
                    ${refLabel}
                    <div style="display: flex; flex-direction: column; gap: 8px; flex: 1; min-height: 0; width: 100%;">
                        ${images.map(src => html `
                            <img
                                src="${src}"
                                style="flex: 1; min-height: 0; width: 100%; object-fit: contain;"
                                alt="Test instruction image"
                            />
                        `)}
                    </div>
                `;
            }
            return html `
                ${refLabel}
                ${images.map(src => html `
                    <img
                        src="${src}"
                        style="${imgStyle}"
                        alt="Test instruction image"
                    />
                `)}
            `;
        }
        else {
            return html ``;
        }
    }
    render() {
        return html `
            <sh-page scrollable='true' theme="${this.currentTheme}">
                <sh-grid fit-content='true'>
                    <sh-card columns='3' style="margin:0px; width: 400px">
                        <sh-card id='Control-Section' label='Controls' style='height: 220px; width: 100%;'>
                             <div style='display: flex; justify-content: space-between; padding-bottom: 24px;'>
                                <sh-button class='my-button' label='${(this.areSystemTestsRunning ? 'End' : 'Start')}' ?disabled='${this.shouldDisableStartSystemTests()}' @click=${() => this.onClickStartStop()}></sh-button>
                                <sh-button class='my-button' label='Pass' ?disabled='${(!this.areSystemTestsRunning)}' @click=${() => this.onClickPass()}></sh-button>
                                <sh-button class='my-button' label='Fail' ?disabled='${(!this.areSystemTestsRunning)}' @click=${() => this.onClickFail()}></sh-button>
                                <sh-button class='my-button' label='Skip' ?disabled='${(!this.areSystemTestsRunning)}' @click=${() => this.onClickSkip()}></sh-button>
                            </div>
                            <sh-modal label="${this.warningModalTitle}"
                                sticky
                                modal-height="250px"
                                modal-width="700px"
                                type="error"
                                ?visible=${this.hasWarning}>
                                <sh-button slot="footer" color="tertiary" label="Close" @click=${this.closeWarningBox}></sh-button>
                            </sh-modal>
                            ${this.shouldDisableStartSystemTests() ? html `
                                <div style="margin-bottom: 10px;">
                                  <sh-text size="body-2" style="color: darkorange;">
                                    <strong>Required:</strong> Please fill in Tester Name, Serial Number, and all Subsystem Serial Numbers before starting tests.
                                  </sh-text>
                                </div>
                            ` : html ``}
                            <div style="display: flex; justify-content: center;">
                                <sh-button size='l' style="padding: 0 8px;" label="Manage Systems" ?disabled='${this.areSystemTestsRunning}' @click=${() => this.initiateAddOrRemoveSystems()}></sh-button>
                            </div>
                            <sh-modal label="Add or Remove Systems" 
                                sticky 
                                modal-height="500px"
                                modal-width="1300px"
                                ?visible=${this.addingSystems}>
                                <sh-table id="add systems table">
                                    <sh-table-row slot="header">
                                        <sh-table-head unsortable columns="1">
                                            <sh-checkbox label="" disabled></sh-checkbox>
                                        </sh-table-head>
                                        <sh-table-head unsortable columns="6">System Name</sh-table-head>
                                        <sh-table-head unsortable columns="5">Health System</sh-table-head>
                                        <sh-table-head unsortable columns="4">Address</sh-table-head>
                                        <sh-table-head unsortable columns="3">Type</sh-table-head>
                                        <sh-table-head unsortable columns="5">Status</sh-table-head>
                                    </sh-table-row>
                                    <div style="overflow-y: auto; height: 500px;">
                                        ${this.systemTesterDisplayFacade.allSystemsList.map((system) => html `
                                            <sh-table-row>
                                                <sh-table-cell columns="1">
                                                    <sh-checkbox label="" 
                                                        ?active="${this.activeSystemsList.find(activeSystem => activeSystem.systemId == system.systemId) != undefined}"
                                                        @checked="${(e) => this.systemSelectionChanged(e, system)}"></sh-checkbox>
                                                </sh-table-cell>
                                                <sh-table-cell columns="6">
                                                    <sh-text>${system.systemName}</sh-text>
                                                </sh-table-cell>
                                                <sh-table-cell columns="5">
                                                    <sh-text>${system.hospitalName}</sh-text>
                                                </sh-table-cell>
                                                <sh-table-cell columns="4">
                                                    <sh-text>${system.address}</sh-text>
                                                </sh-table-cell>
                                                <sh-table-cell columns="3">
                                                    <sh-text>${system.isControlSide ? 'Control-side' : 'Patient-side'}</sh-text>
                                                </sh-table-cell>
                                                <sh-table-cell columns="5">
                                                    <cor-system-state state="${system.systemState}"></cor-system-state>
                                                </sh-table-cell>
                                            </sh-table-row>
                                        `)}
                                    </div>
                                </sh-table>
                                <sh-button label="close" slot="footer" @click=${() => this.closeAddOrRemoveSystems()}></sh-button>
                            </sh-modal>
                        </sh-card>

                        ${this.getActiveSystemsListDisplay()}

                        <sh-card id='Test-Section' label='Tests' style='height: 100%; width: 100%;'>
                            <div style="text-align: flex-start; display: flex; flex-direction: column; gap: 22px;">
                                ${this.parentTestResults.length > 0
            ? this.parentTestResults.map((parentTestResult, i) => html `
                                        <div style="display: flex; flex-direction: column; gap: 6px; align-items: stretch;">
                                            <div style="display: flex; align-items: center; justify-content: flex-start; gap: 8px;">
                                                <sh-text class="test-text" size='body-1' color='primary' ?disabled='${this.disabledStates[i]}' style="${this.isCurrentParentTest(i) ? 'font-weight: bold; color: #000;' : ''}">${this.getParentTestDisplayName(i)}</sh-text>
                                                <sh-icon icon="success" ?disabled='${this.disabledPasses[i]}' size="s" style="padding: 0 5px;"></sh-icon>
                                                <sh-icon icon="error" ?disabled='${this.disabledFailures[i]}' size="s" style="padding: 0 5px;"></sh-icon>
                                            </div>
                                    </div>
                                    `)
            : [...Array(this.testLimit)].map((_, i) => html `
                                        <div style="display: flex; flex-direction: column; gap: 6px; align-items: stretch;">
                                            <div style="display: flex; align-items: center; justify-content: center; gap: 8px;">
                                                <sh-text class="test-text" size='body-1' color='primary' ?disabled='${this.disabledStates[i]}' style="${this.isCurrentParentTest(i) ? 'font-weight: bold; color: #000;' : ''}">${this.getParentTestDisplayName(i)}</sh-text>
                                                <sh-icon icon="success" ?disabled='${this.disabledPasses[i]}' size="s" style="padding: 0 5px;"></sh-icon>
                                                <sh-icon icon="error" ?disabled='${this.disabledFailures[i]}' size="s" style="padding: 0 5px;"></sh-icon>
                                            </div>
                                        </div>
                                    `)}
                            </div>
                        </sh-card>

                        <sh-card label='Tester Name'>
                            <div style='display: flex; flex-direction: column; gap: 10px;'>
                                <sh-input-text
                                    label='First Name'
                                    placeholder='First Name'
                                    value="${this.testerFirstName}"
                                    ?readonly=${this.areSystemTestsRunning}
                                    @input=${(e) => this.handleTesterFirstNameInput(e)}
                                ></sh-input-text>
                                <sh-input-text
                                    label='Last Name'
                                    placeholder='Last Name'
                                    value="${this.testerLastName}"
                                    ?readonly=${this.areSystemTestsRunning}
                                    @input=${(e) => this.handleTesterLastNameInput(e)}
                                ></sh-input-text>
                            </div>
                        </sh-card>

                        <sh-card label='System Serial Number'>
                            <div style='display: flex; flex-direction: column; gap: 10px;'>
                                <sh-input-text
                                    label='System Serial Number'
                                    placeholder='Enter alphanumeric serial number'
                                    value="${this.serialNumber}"
                                    ?readonly=${this.areSystemTestsRunning}
                                    @input=${(e) => this.handleSerialNumberInput(e)}
                                    helper-text='Only letters and numbers allowed'
                                ></sh-input-text>
                            </div>
                        </sh-card>

                        <sh-card label='Subsystem Serial Numbers'>
                            <div style='display: flex; flex-direction: column; gap: 10px;'>
                                ${this.subsystemNames.map((subsystemName) => html `
                                    <sh-input-text
                                        label='${subsystemName} Serial Number'
                                        placeholder='Enter alphanumeric serial number'
                                        value="${this.subsystemSerialNumbers[subsystemName] || ''}"
                                        ?readonly=${this.areSystemTestsRunning}
                                        @input=${(e) => this.handleSubsystemSerialNumberInput(e, subsystemName)}
                                        helper-text='Only letters and numbers allowed'
                                    ></sh-input-text>
                                `)}
                            </div>
                        </sh-card>
                    </sh-card>

                    <div
                        style="
                            width: 1250px;
                            height: calc(100vh - 40px);
                            display: flex;
                            flex-direction: column;
                            gap: 16px;
                        "
                    >
                        <!-- TEST INSTRUCTIONS -->
                        <sh-card
                            id="Test-Instructions-Section"
                            label="Test Instructions"
                            style="flex: 1; min-height: 0; width: 100%;"
                        >
                            <div style="display: flex; gap: 16px; height: 100%;">

                                <!-- Work Instruction Frame -->
                                <div style="
                                    flex: 0 0 35%;
                                    border: 1px solid #d8dde6;
                                    border-radius: 4px;
                                    padding: 16px;
                                    display: flex;
                                    flex-direction: column;
                                    gap: 12px;
                                    overflow-y: auto;
                                ">
                                    <sh-text size="body-2" style="font-weight: bold; color: black;">Work Instruction</sh-text>
                                    <div id="instructionsContainer" style="
                                        flex: 1;
                                        display: flex;
                                        flex-direction: column;
                                        gap: 8px;
                                        font-size: 40px;
                                        justify-content: flex-start;
                                        align-items: flex-start;
                                    ">
                                        ${this.getCurrentInstruction()}
                                    </div>
                                </div>

                                <!-- Image (no frame, full remaining space) -->
                                <div style="
                                    flex: 1;
                                    display: flex;
                                    flex-direction: column;
                                    min-height: 0;
                                    align-items: stretch;
                                ">
                                    ${this.renderInstructionImages()}
                                </div>

                            </div>
                        </sh-card>

                        <!-- SYSTEM FAULT MESSAGE VIEWER -->
                        <sh-card
                            label="System Fault Message Viewer"
                            style="height: 450px; width: 100%;"
                        >
                            ${this.renderFaultMessageLines()}
                        </sh-card>
                    </div>

                </sh-grid>
            </sh-page>
        `;
    }
};
CorPageSystemTester.COLUMN_WIDTH = 12;
CorPageSystemTester.TOTAL_COLUMNS = 36;
__decorate([
    property()
], CorPageSystemTester.prototype, "currentTheme", void 0);
__decorate([
    property()
], CorPageSystemTester.prototype, "hasWarning", void 0);
__decorate([
    property()
], CorPageSystemTester.prototype, "warningModalTitle", void 0);
__decorate([
    property()
], CorPageSystemTester.prototype, "addingSystems", void 0);
__decorate([
    property()
], CorPageSystemTester.prototype, "areSystemTestsRunning", void 0);
__decorate([
    property()
], CorPageSystemTester.prototype, "activeSystemsList", void 0);
__decorate([
    property()
], CorPageSystemTester.prototype, "controlSystemCount", void 0);
__decorate([
    property()
], CorPageSystemTester.prototype, "patientSystemCount", void 0);
__decorate([
    property()
], CorPageSystemTester.prototype, "currentRunningTest", void 0);
__decorate([
    property()
], CorPageSystemTester.prototype, "messageText", void 0);
__decorate([
    property()
], CorPageSystemTester.prototype, "logEntries", void 0);
__decorate([
    property()
], CorPageSystemTester.prototype, "disabledStates", void 0);
__decorate([
    property()
], CorPageSystemTester.prototype, "disabledPasses", void 0);
__decorate([
    property()
], CorPageSystemTester.prototype, "disabledFailures", void 0);
__decorate([
    property()
], CorPageSystemTester.prototype, "callAfterWarningClose", void 0);
__decorate([
    property()
], CorPageSystemTester.prototype, "testEndedMessageDisabled", void 0);
__decorate([
    property()
], CorPageSystemTester.prototype, "systemTestRuntime", void 0);
__decorate([
    property()
], CorPageSystemTester.prototype, "subsystemSerialNumbers", void 0);
__decorate([
    property()
], CorPageSystemTester.prototype, "serialNumber", void 0);
__decorate([
    property()
], CorPageSystemTester.prototype, "isSerialNumberLocked", void 0);
__decorate([
    property()
], CorPageSystemTester.prototype, "isSubsystemSerialNumbersLocked", void 0);
__decorate([
    property()
], CorPageSystemTester.prototype, "parentTestResults", void 0);
__decorate([
    property()
], CorPageSystemTester.prototype, "testerFirstName", void 0);
__decorate([
    property()
], CorPageSystemTester.prototype, "testerLastName", void 0);
__decorate([
    property()
], CorPageSystemTester.prototype, "isTesterNameLocked", void 0);
CorPageSystemTester = CorPageSystemTester_1 = __decorate([
    customElement('cor-system-tester')
], CorPageSystemTester);
export { CorPageSystemTester };
//# sourceMappingURL=CorPageSystemTester.js.map